import React, { useCallback, useEffect, useState } from "react";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Snackbar,
  Divider,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { getTenantBranding, saveTenantBranding } from "api/tenantBrandingApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * /settings/branding — tenant whitelabel admin page.
 *
 * Org name + colors + contact info + legal URLs + audit-packet footer.
 * Sparse PUT — only non-empty fields are written; cleared fields ARE sent
 * (server treats blank as null and clears the column).
 */

const emptyForm = {
  orgName: "",
  logoUrl: "",
  primaryColor: "",
  accentColor: "",
  contactEmail: "",
  supportPhone: "",
  dispatchPhone: "",
  termsUrl: "",
  privacyUrl: "",
  auditPacketFooter: "",
};

const Section = ({ title, children }) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
      <Stack spacing={2}>{children}</Stack>
    </CardContent>
  </Card>
);

const Preview = ({ form }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: "#ffffff" }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">Preview</Typography>
      <Box
        sx={{
          mt: 1,
          p: 2,
          borderRadius: 2,
          backgroundColor: form.primaryColor || "#1F4E78",
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {form.logoUrl ? (
          <Box
            component="img"
            src={form.logoUrl}
            alt="logo"
            sx={{ height: 36, width: "auto", borderRadius: 1, backgroundColor: "#ffffff", p: 0.5 }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : null}
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {form.orgName || "Your Org Name"}
        </Typography>
      </Box>
      <Box sx={{ mt: 2 }}>
        {form.accentColor ? (
          <Button
            variant="contained"
            disableElevation
            sx={{ backgroundColor: form.accentColor, "&:hover": { backgroundColor: form.accentColor, opacity: 0.9 } }}
          >
            Accent button
          </Button>
        ) : (
          <Button variant="contained" disableElevation>Accent button</Button>
        )}
      </Box>
      {form.contactEmail || form.supportPhone ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          {form.contactEmail}
          {form.contactEmail && form.supportPhone ? " · " : ""}
          {form.supportPhone}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

export const TenantBrandingPage = () => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTenantBranding();
      const data = unwrapObject(res) || {};
      setForm({ ...emptyForm, ...Object.fromEntries(
        Object.entries(data).filter(([_, v]) => v != null),
      ) });
    } catch (e) {
      setError(e?.message || "Failed to load branding");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveTenantBranding(form);
      setToast("Branding saved");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Branding & Whitelabel
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Stamp your org's identity on emails, audit packets, and the admin/driver apps. All fields are optional.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={load} disabled={saving}>
            Discard
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} sx={{ color: "white" }} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ backgroundColor: "#1F4E78" }}
          >
            Save
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: 2, minWidth: 320 }}>
          <Stack spacing={2}>
            <Section title="Identity">
              <TextField label="Organization name" size="small" value={form.orgName} onChange={set("orgName")} fullWidth />
              <TextField
                label="Logo URL"
                size="small"
                value={form.logoUrl}
                onChange={set("logoUrl")}
                helperText="Public URL of a square logo image (PNG/SVG). Paste any CDN or hosted URL."
                fullWidth
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Primary color"
                  size="small"
                  value={form.primaryColor}
                  onChange={set("primaryColor")}
                  placeholder="#1F4E78"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Accent color"
                  size="small"
                  value={form.accentColor}
                  onChange={set("accentColor")}
                  placeholder="#0ea5e9"
                  sx={{ flex: 1 }}
                />
              </Box>
            </Section>

            <Section title="Contact">
              <TextField label="Contact email" type="email" size="small" value={form.contactEmail} onChange={set("contactEmail")} fullWidth />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField label="Support phone" size="small" value={form.supportPhone} onChange={set("supportPhone")} sx={{ flex: 1 }} />
                <TextField label="Dispatch phone" size="small" value={form.dispatchPhone} onChange={set("dispatchPhone")} sx={{ flex: 1 }} />
              </Box>
            </Section>

            <Section title="Legal">
              <TextField label="Terms of Service URL" size="small" value={form.termsUrl} onChange={set("termsUrl")} fullWidth />
              <TextField label="Privacy Policy URL" size="small" value={form.privacyUrl} onChange={set("privacyUrl")} fullWidth />
            </Section>

            <Section title="Audit packet footer">
              <Typography variant="caption" color="text.secondary">
                Appended to the summary.txt in every driver Audit Packet ZIP. Use it for a corporate signature, certifying statement, or attestation language brokers expect.
              </Typography>
              <TextField
                multiline
                rows={4}
                size="small"
                value={form.auditPacketFooter}
                onChange={set("auditPacketFooter")}
                placeholder="e.g. — Certified by [Your Org] compliance team. For audit questions: compliance@yourorg.com"
                fullWidth
              />
            </Section>
          </Stack>
        </Box>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Preview form={form} />
          <Box sx={{ mt: 2 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Where this shows up</Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="caption">• Audit Packet ZIP summary.txt header + footer</Typography>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography variant="caption">• Driver app login screen (org name + logo)</Typography>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography variant="caption">• Admin web header (org name + colors)</Typography>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography variant="caption">• Outgoing email templates (signature + contact line)</Typography>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast || ""}
      />
    </Stack>
  );
};

export default TenantBrandingPage;
